<template>
  <div class="user_list_page">

    <business-group 
      v-if="isDeviceModelsLoaded"
      :tableConfig="tableConfig" 
      :formConfig="{ ...formConfig }" 
      :formValues="dynamicFormValues"
      ref="businessGroup"
    >
      <!-- 表格上方操作按钮 -->
      <template #tableAction>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div style="display: flex; align-items: center;">
            <a-select 
              placeholder="请选择设备型号" 
              v-model="selectedDeviceModel"
              style="width: 250px;" 
              allowClear
              @change="handleDeviceModelChange"
            >
              <a-select-option v-for="model in deviceModels" :key="model.deviceModel"
                :value="model.deviceModel">
                <span style="display: inline-block; width: 80px;">{{ model.modelName }}</span>
                <span>{{ model.deviceDescription }}</span>
              </a-select-option>
            </a-select>
          </div>
          <div>
            <a-button type="primary" @click="handleAppStyle" :disabled="!selectedDeviceModel">
              <a-icon type="appstore" />
              APP样式
            </a-button>
          </div>
        </div>
      </template>
      
      <!-- 查询 -->
      <template #formSlot>
        <a-form-item>
          <a-input placeholder="请输入场景名称" v-decorator="['effectName']"
            style="width: 200px; margin-right: 10px; vertical-align: middle" />
        </a-form-item>
        <a-form-item>
          <a-select placeholder="请选择场景所属类别" v-decorator="['effectType']"
            style="width: 200px; margin-right: 10px; vertical-align: middle" allowClear>
            <a-select-option v-for="item in effectTypeList" :key="item.effectType" :value="item.effectType">
              {{ item.effectType }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-select placeholder="请选择光效应用版本" v-decorator="['effectVersion']"
            style="width: 200px; margin-right: 10px; vertical-align: middle" allowClear>
            <a-select-option v-for="item in effectVersionList" :key="item.version" :value="item.version">
              V{{ item.version }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </template>

      <!-- 创建时间格式化 -->
      <template #createTime="record">
        {{ dataFormatStr(record.createTime, "YYYY-MM-DD HH:mm:ss") }}
      </template>

      <!-- 场景速度格式化 -->
      <template slot="effectOrder" slot-scope="record">
        {{ formatEffectOrder(record.effectOrder) }}
      </template>

      <!-- 光效版本格式化 -->
      <template slot="effectVersion" slot-scope="record">
        {{ record.effectVersion ? `V${record.effectVersion}` : record.effectVersion }}
      </template>

      <!-- 未选中图标格式化 -->
      <template slot="icon" slot-scope="record">
        <img width="30" :src="record.icon" />
      </template>

      <!-- 选中图标格式化 -->
      <template slot="selectedIcon" slot-scope="record">
        <img width="30" :src="record.selectedIcon" />
      </template>

    </business-group>

    <!-- APP样式弹窗 -->
    <a-modal
      title="APP样式预览"
      :visible="appStyleModalVisible"
      :footer="null"
      width="375px"
      :bodyStyle="{ padding: '0', backgroundColor: '#1a1a1a' }"
      :wrapClassName="'app-style-dark-modal'"
      @cancel="handleAppStyleCancel"
    >
      <div v-if="appStyleData.length > 0" class="dark-mode-container">
        <!-- 顶部导航栏 (Top Navigation Bar) -->
        <div class="tabs-navigation">
          <div 
            v-for="(group, index) in appStyleData" 
            :key="index"
            class="tab-item"
            :class="{ 'tab-active': activeTabIndex === index }"
            @click="activeTabIndex = index"
          >
            {{ group.effectType }}
          </div>
        </div>

        <!-- 主体内容区 (Main Content Grid) -->
        <div class="content-grid">
          <div 
            v-for="item in currentGroupItems" 
            :key="item.serialNumber"
            class="grid-item"
            :class="{ 'grid-selected': selectedSerialNumber === item.serialNumber }"
            @click="selectItem(item)"
          >
            <!-- 多速度标识图标 -->
            <img 
              v-if="item.effectOrder && Array.isArray(item.effectOrder) && item.effectOrder.length > 1"
              :src="require('@/assets/images/icon/mult_effect_order.png')"
              class="mult-effect-badge"
            />
            <img 
              :src="selectedSerialNumber === item.serialNumber ? item.selectedIcon : item.icon" 
              class="item-icon"
            />
            <span class="item-name">{{ item.effectName }}</span>
          </div>
        </div>
      </div>
      <a-empty v-else description="暂无数据" :imageStyle="{ filter: 'invert(1)' }" />
    </a-modal>

  </div>
</template>

<script>
import pageTitle from "@/components/business/pageTitle";
import businessGroup from "@/components/business/businessGroup";
import { tips } from "@/utils/util";
import { formatToStr } from "@/utils/dateUtils";

export default {
  components: {
    pageTitle,
    businessGroup
  },
  data() {
    return {
      tableConfig: {
        url: "bluetoothlight/web/router/neewerhome/manage/device/sceneEffect/pages",
        rowKey: "serialNumber",
        columns: [
          {
            title: "场景排序",
            dataIndex: "sortNumber",
            key: "sortNumber",
            align: "center",
            ellipsis: true,
            width: 90
          },
          {
            title: "场景所属类别",
            dataIndex: "effectType",
            key: "effectType",
            align: "center",
            ellipsis: true,
            width: 120
          },
          {
            title: "场景名称",
            dataIndex: "effectName",
            key: "effectName",
            align: "center",
            ellipsis: true,
            width: 200
          },
          {
            title: "场景速度",
            key: "effectOrder",
            align: "center",
            ellipsis: true,
            scopedSlots: { customRender: "effectOrder" },
            width: 100
          },
          {
            title: "光效应用版本",
            key: "effectVersion",
            align: "center",
            ellipsis: true,
            width: 120,
            scopedSlots: { customRender: "effectVersion" }
          },
          {
            title: "未选中图标",
            key: "icon",
            align: "center",
            ellipsis: true,
            scopedSlots: { customRender: "icon" },
            width: 120
          },
          {
            title: "选中图标",
            key: "selectedIcon",
            align: "center",
            ellipsis: true,
            scopedSlots: { customRender: "selectedIcon" },
            width: 120
          },
          {
            title: "创建时间",
            key: "createTime",
            scopedSlots: { customRender: "createTime" },
            align: "center",
            width: 160
          }
        ]
      },
      formConfig: {
        timeShow: false,
        formShow: true
      },
      effectTypeList: [], // 场景类别下拉列表数据（查询表单用）
      effectVersionList: [], // 光效应用版本下拉列表数据
      deviceModels: [], // 设备型号选项数组
      selectedDeviceModel: undefined, // 默认选中的设备型号
      selectedDeviceType: undefined, // 默认选中的设备类型
      formValues: {}, // 额外的查询参数
      isDeviceModelsLoaded: false, // 设备型号是否已加载完成
      // APP样式弹窗相关
      appStyleModalVisible: false, // APP样式弹窗显示状态
      appStyleLoading: false, // APP样式加载状态
      appStyleData: [], // APP样式数据
      activeTabIndex: 0, // 当前激活的Tab索引
      selectedSerialNumber: null // 当前选中的场景序号
    };
  },
  computed: {
    // 动态生成表单查询参数
    dynamicFormValues() {
      const values = { ...this.formValues };
      // 如果选择了设备型号，添加到查询参数中
      if (this.selectedDeviceModel) {
        values.deviceModel = this.selectedDeviceModel;
      }
      // 如果选择了设备类型，添加到查询参数中
      if (this.selectedDeviceType) {
        values.deviceType = this.selectedDeviceType;
      }
      return values;
    },
    // APP样式弹窗中的表格列配置
    appStyleColumns() {
      return [
        {
          title: "序号",
          dataIndex: "serialNumber",
          key: "serialNumber",
          align: "center",
          width: 80
        },
        {
          title: "场景排序",
          dataIndex: "sortNumber",
          key: "sortNumber",
          align: "center",
          width: 90
        },
        {
          title: "场景名称",
          dataIndex: "effectName",
          key: "effectName",
          align: "center",
          width: 150
        },
        {
          title: "场景速度",
          key: "effectOrder",
          align: "center",
          scopedSlots: { customRender: "effectOrder" },
          width: 100
        },
        {
          title: "光效应用版本",
          key: "effectVersion",
          align: "center",
          scopedSlots: { customRender: "effectVersion" },
          width: 120
        },
        {
          title: "未选中图标",
          key: "icon",
          align: "center",
          scopedSlots: { customRender: "icon" },
          width: 100
        },
        {
          title: "选中图标",
          key: "selectedIcon",
          align: "center",
          scopedSlots: { customRender: "selectedIcon" },
          width: 100
        },
        {
          title: "创建时间",
          key: "createTime",
          scopedSlots: { customRender: "createTime" },
          align: "center",
          width: 160
        }
      ];
    },
    // 当前Tab对应的场景列表
    currentGroupItems() {
      if (this.appStyleData.length === 0 || this.activeTabIndex >= this.appStyleData.length) {
        return [];
      }
      return this.appStyleData[this.activeTabIndex].effectNameList || [];
    }
  },
  mounted() {
    // 获取设备型号列表
    this.fetchDeviceModels();
  },
  methods: {
    // 刷新
    refreshData() {
      this.$refs?.["businessGroup"]?.getData?.();
    },
    // 设备型号变化处理
    handleDeviceModelChange(value) {
      console.log("设备型号变化:", value);
      
      if (value) {
        const selectedModel = this.deviceModels.find(model => model.deviceModel === value);
        if (selectedModel) {
          this.selectedDeviceType = selectedModel.deviceType;
          
          // 联动逻辑：型号变更时，重新并发获取下拉数据
          Promise.all([
            this.fetchEffectTypeList(),
            this.fetchEffectVersionList()
          ]).then(() => {
            // 下拉数据更新后，刷新表格列表（入参 deviceModel 已通过计算属性自动更新）
            this.refreshData();
          });
        }
      } else {
        // 清空选择时，重置所有相关状态
        this.selectedDeviceType = undefined;
        this.effectTypeList = [];
        this.effectVersionList = [];
        this.$refs?.["businessGroup"]?.resetFormFields?.(['effectType', 'effectVersion']);
        
        // 如果业务允许在无型号时显示空表格或全量数据，可在此调用 refreshData
        // 若必须要求有型号才能查询，则不调用
      }
    },
    // 获取设备型号列表
    async fetchDeviceModels() {
      try {
        const result = await this.$get("bluetoothlight/web/router/neewerhome/manage/device/classification/get");
        if (result?.resultStatus && result.resultData) {
          this.deviceModels = result.resultData;
          if (this.deviceModels.length > 0) {
            this.selectedDeviceModel = this.deviceModels[0].deviceModel;
            // 同时设置 deviceType
            this.selectedDeviceType = this.deviceModels[0].deviceType;
            
            // 2. 并发获取两个下拉数据
            await Promise.all([
              this.fetchEffectTypeList(),
              this.fetchEffectVersionList()
            ]);
          }
        } else {
          console.warn("获取设备型号列表为空或失败");
        }
      } catch (error) {
        console.error("获取设备型号失败:", error);
        tips(this, false, "获取设备型号失败");
      } finally {
        // 3. 无论成功与否，都标记为已加载，触发表格渲染（第3步）
        // 此时 dynamicFormValues 中已包含 deviceModel，表格会自动发起请求
        this.isDeviceModelsLoaded = true;
      }
    },
    // 获取场景类别下拉列表
    async fetchEffectTypeList() {
      // 如果没有选择设备型号，则不请求接口
      if (!this.selectedDeviceModel || !this.selectedDeviceType) {
        this.effectTypeList = [];
        return;
      }

      try {
        const result = await this.$get("bluetoothlight/web/router/neewerhome/manage/device/diySceneEffect/getEffectTypeDropdownList", {
          deviceType: this.selectedDeviceType,
          deviceModel: this.selectedDeviceModel
        });
        if (result?.resultStatus && result.resultData) {
          this.effectTypeList = result.resultData;
        } else {
          console.warn("获取场景类别列表失败:", result?.message);
          this.effectTypeList = [];
        }
      } catch (error) {
        console.error("获取场景类别列表异常:", error);
        tips(this, false, "获取场景类别列表失败");
        this.effectTypeList = [];
      }
    },
    // 获取光效应用版本下拉列表
    async fetchEffectVersionList() {
      // 如果没有选择设备型号，则不请求接口
      if (!this.selectedDeviceModel || !this.selectedDeviceType) {
        this.effectVersionList = [];
        return;
      }

      try {
        const result = await this.$get("bluetoothlight/web/router/neewerhome/manage/device/diySceneEffect/getEffectVersionDropdownList", {
          deviceType: this.selectedDeviceType,
          deviceModel: this.selectedDeviceModel
        });
        if (result?.resultStatus && result.resultData) {
          this.effectVersionList = result.resultData;
        } else {
          console.warn("获取光效应用版本列表失败:", result?.message);
          this.effectVersionList = [];
        }
      } catch (error) {
        console.error("获取光效应用版本列表异常:", error);
        tips(this, false, "获取光效应用版本列表失败");
        this.effectVersionList = [];
      }
    },
    // 时间格式化
    dataFormatStr(ret, format) {
      if (!ret) {
        return "";
      }
      if (!format) {
        format = "YYYY-MM-DD HH:mm:ss";
      }
      return formatToStr(ret, format);
    },
    // 场景速度格式化（处理数组格式）
    formatEffectOrder(effectOrder) {
      if (!effectOrder || !Array.isArray(effectOrder) || effectOrder.length === 0) {
        return '';
      }
      // 将数组中的数字转换为对应的文字描述
      const orderMap = {
        1: '标准',
        2: '慢',
        3: '快'
      };
      return effectOrder.map(order => orderMap[order] || order).join('、');
    },
    // 点击APP样式按钮
    async handleAppStyle() {
      if (!this.selectedDeviceModel || !this.selectedDeviceType) {
        tips(this, false, "请先选择设备型号");
        return;
      }

      this.appStyleModalVisible = true;
      this.appStyleLoading = true;
      this.appStyleData = [];
      this.activeTabIndex = 0;
      this.selectedSerialNumber = null;

      try {
        // 获取筛选表单中的光效应用版本值
        let effectVersion = null;
        const formInstance = this.$refs.businessGroup?.form;
        if (formInstance) {
          effectVersion = formInstance.getFieldValue('effectVersion');
        }

        // 构建请求参数
        const params = {
          deviceType: this.selectedDeviceType,
          deviceModel: this.selectedDeviceModel
        };
        
        // 如果选择了光效应用版本，添加到参数中
        if (effectVersion) {
          params.effectVersion = effectVersion;
        }

        const result = await this.$get("bluetoothlight/web/router/neewerhome/manage/device/sceneEffect/all", params);

        if (result?.resultStatus && result.resultData) {
          this.appStyleData = result.resultData;
        } else {
          tips(this, false, result?.errorMessage || "获取APP样式数据失败");
          this.appStyleData = [];
        }
      } catch (error) {
        console.error("获取APP样式数据异常:", error);
        tips(this, false, "获取APP样式数据失败");
        this.appStyleData = [];
      } finally {
        this.appStyleLoading = false;
      }
    },
    // 选中场景项
    selectItem(item) {
      this.selectedSerialNumber = item.serialNumber;
    },
    // APP样式弹窗确定
    handleAppStyleOk() {
      this.appStyleModalVisible = false;
    },
    // APP样式弹窗取消
    handleAppStyleCancel() {
      this.appStyleModalVisible = false;
      this.appStyleData = [];
      this.activeTabIndex = 0;
      this.selectedSerialNumber = null;
    }
  }
};
</script>

<style scoped>
.user_list_page {
  padding: 20px;
}
</style>

<style>
/* APP样式弹窗 - 深色模式全局样式（非scoped） */
.app-style-dark-modal .ant-modal-content {
  background-color: #1a1a1a;
  border-radius: 12px;
}

.app-style-dark-modal .ant-modal-header {
  background-color: #2a2a2a;
  border-bottom: 1px solid #333333;
  border-radius: 12px 12px 0 0;
  padding: 16px 20px;
}

.app-style-dark-modal .ant-modal-title {
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
}

.app-style-dark-modal .ant-modal-close {
  color: #999999;
}

.app-style-dark-modal .ant-modal-close:hover {
  color: #4fc3f7;
}

.app-style-dark-modal .ant-modal-body {
  padding: 0;
  background-color: #1a1a1a;
  border-radius: 0 0 12px 12px;
}

.app-style-dark-modal .ant-empty-description {
  color: #999999;
}
</style>

<style scoped>
/* APP样式弹窗 - 深色模式 */
.dark-mode-container {
  background-color: #1a1a1a;
  min-height: 500px;
  color: #ffffff;
}

.tabs-navigation {
  display: flex;
  border-bottom: 1px solid #333333;
  margin-bottom: 15px;
  padding: 0 10px;
  background-color: #1a1a1a;
  overflow-x: auto;
}

.tab-item {
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #999999;
  position: relative;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-item:hover {
  color: #4fc3f7;
}

.tab-active {
  color: #4fc3f7;
  font-weight: bold;
}

.tab-active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #4fc3f7;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 10px;
  max-height: 450px;
  overflow-y: auto;
  background-color: #1a1a1a;
}

.grid-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 4px;
  border: 1px solid #333333;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #2a2a2a;
  position: relative;
}

.mult-effect-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  z-index: 10;
}

.grid-item:hover {
  border-color: #4fc3f7;
  box-shadow: 0 2px 8px rgba(79, 195, 247, 0.3);
  background-color: #333333;
}

.grid-selected {
  border-color: #4fc3f7;
  background-color: #1e3a5f;
  box-shadow: 0 2px 12px rgba(79, 195, 247, 0.4);
}

.item-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 6px;
}

.item-name {
  font-size: 11px;
  color: #cccccc;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 滚动条样式 - 深色模式 */
.content-grid::-webkit-scrollbar {
  width: 6px;
}

.content-grid::-webkit-scrollbar-track {
  background: #1a1a1a;
}

.content-grid::-webkit-scrollbar-thumb {
  background: #444444;
  border-radius: 3px;
}

.content-grid::-webkit-scrollbar-thumb:hover {
  background: #555555;
}

.tabs-navigation::-webkit-scrollbar {
  height: 4px;
}

.tabs-navigation::-webkit-scrollbar-track {
  background: #1a1a1a;
}

.tabs-navigation::-webkit-scrollbar-thumb {
  background: #444444;
  border-radius: 2px;
}
</style>
